/**
 * Ria document-parsing service.
 *
 * POST /api/v1/ria/transactions/parse-documents — sends the RPA/CBS (+ any
 * counter offers) as multipart/form-data and gets back the reconciled
 * { timeline, parties, reconciliation, propertyAddress? } shape used by the
 * Buying/Listing create-last review flow.
 *
 * Two independent retry layers, matching rialty-mobile-app's
 * src/services/ria.service.ts:
 *
 *   Layer 1 (network) — configured on the apiClient `retry` option below.
 *     Up to 10 retries, but ONLY on a pure network error (no response at
 *     all — dropped socket, offline). Never on a timeout: at 300s the bytes
 *     have likely already reached the server and a retry would re-run the
 *     90-120s LLM job. Exponential backoff capped at 30s. Each retry rebuilds
 *     FormData from the original file/blob sources rather than resending a
 *     body the HTTP client may have already consumed.
 *
 *   Layer 2 (application) — the outer loop in parseTransactionDocuments,
 *     up to 4 total attempts. Retries two soft-failure modes the transport
 *     layer can't see:
 *       1. `timeline: []` in an HTTP 200 — the LLM occasionally misses on
 *          the first pass and succeeds on a second/third with identical
 *          input. No delay — parse is already 90-120s per attempt.
 *       2. Vertex AI quota exhaustion — gateway 502, or a response message
 *          matching /RESOURCE_EXHAUSTED|Vertex AI.*failed.*429/i. Backs off
 *          5s -> 10s -> 20s (capped at 30s) to ride out the quota window.
 *     Any other failure (401/403, non-Vertex 5xx, ERR_NETWORK after Layer 1
 *     exhausts its own retries, a parties-empty-but-timeline-ok response,
 *     etc.) throws immediately — it is not retried here.
 */
import { API_ENDPOINTS, apiClient, isNetworkErrorOnly } from '../config/api';

const MAX_ATTEMPTS = 4;

/**
 * @typedef {Object} ParsePart
 * @property {Blob|File} blob   The file content (a picked File or a downloaded Blob).
 * @property {string} name      Filename sent as the multipart part's filename.
 * @property {string} label     'RPA' | 'CBS' | 'COUNTER_OFFER'
 */

// Blob/File instances aren't consumed by being appended to a FormData or by
// an XHR send in the browser, so "rebuilding from file sources" just means
// wrapping the same in-memory blob references in a brand new FormData —
// there's no need to re-download or re-pick anything.
const buildFormData = (side, parts) => {
  const fd = new FormData();
  fd.append('side', side);
  parts.forEach(({ blob, name, label }) => {
    fd.append('files', blob, name);
    fd.append('labels', label);
  });
  return fd;
};

async function parseTransactionDocumentsOnce(side, parts, { firebaseToken, onProgress } = {}) {
  const response = await apiClient.post(
    API_ENDPOINTS.riaParseDocuments,
    buildFormData(side, parts),
    {
      headers: {
        ...(firebaseToken ? { Authorization: `Bearer ${firebaseToken}` } : {}),
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 min per attempt — AI parse legitimately takes 90-120s
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)); }
        : undefined,
      retry: {
        retries: 10,
        retryOn: isNetworkErrorOnly,
        retryDelayMs: 1000,
        buildData: () => buildFormData(side, parts),
      },
    }
  );

  const data = response.data;
  const timelineOk = Array.isArray(data?.timeline) && data.timeline.length > 0;
  const partiesOk = Array.isArray(data?.parties) && data.parties.length > 0;
  if (!timelineOk || !partiesOk) {
    const reasons = [];
    if (!Array.isArray(data?.timeline)) reasons.push('timeline missing or not an array');
    else if (data.timeline.length === 0) reasons.push('timeline empty');
    if (!Array.isArray(data?.parties)) reasons.push('parties missing or not an array');
    else if (data.parties.length === 0) reasons.push('parties empty');
    const err = new Error(`Parse response was empty or malformed (${reasons.join('; ')}).`);
    err.config = { url: API_ENDPOINTS.riaParseDocuments };
    err.response = { data };
    throw err;
  }
  return data;
}

/**
 * Send RPA/CBS + counter-offer documents to the parse endpoint. `parts` must
 * be in caller-supplied order — RPA/CBS first, then counter offers — so the
 * server's files[i]/labels[i] stay aligned.
 *
 * @param {'BUYING'|'LISTING'} side
 * @param {ParsePart[]} parts
 * @param {{ firebaseToken?: string, onProgress?: (pct:number)=>void }} [options]
 * @returns {Promise<{timeline:any[], parties:any[], reconciliation:any, propertyAddress?:any}>}
 */
export async function parseTransactionDocuments(side, parts, options = {}) {
  let lastRetryableErr = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await parseTransactionDocumentsOnce(side, parts, options);
    } catch (err) {
      const msg = String(err?.message || err?.response?.data?.message || '');
      const status = err?.response?.status;
      const isEmptyTimeline = msg.includes('timeline empty');
      // Vertex AI quota exhaustion surfaces as a gateway 502 wrapping a 429
      // RESOURCE_EXHAUSTED from Google — match either signal.
      const isVertexQuota = status === 502 || /RESOURCE_EXHAUSTED|Vertex AI.*failed.*429/i.test(msg);
      const retryable = isEmptyTimeline || isVertexQuota;

      if (retryable && attempt < MAX_ATTEMPTS) {
        const delayMs = isVertexQuota ? Math.min(5000 * Math.pow(2, attempt - 1), 30000) : 0;
        console.warn(
          `[RIA] parse retry ${attempt}/${MAX_ATTEMPTS} in ${delayMs}ms — `
          + `${isVertexQuota ? 'Vertex quota (502/RESOURCE_EXHAUSTED)' : 'timeline empty'}`
        );
        lastRetryableErr = err;
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        continue;
      }
      throw err;
    }
  }
  // Loop either returns or throws above — unreachable, kept for safety.
  throw lastRetryableErr ?? new Error('parseTransactionDocuments: exhausted retries');
}
